/**
 * Kmart SDK 初始化
 * 立即创建 kmartApp 对象，如果原生未注入则方法调用返回空数据
 */

// 最大等待时间（毫秒）- 用于轮询检查原生是否注入
const MAX_WAIT_TIME = 3000
// 轮询间隔（毫秒）
const POLL_INTERVAL = 50

// 标记原生是否已连接
let nativeConnected = false

//生成随机字符
const nonce = function (length) {
	const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
	const values = new Uint8Array(length)
	crypto.getRandomValues(values)
	return Array.from(values)
		.map(value => charset[value % charset.length])
		.join('')
}

//回调映射
const callbackMap = {}

//设置回调
window.__kmart_native_host_callback__ = function (base64) {
	try {
		let text = atob(base64)
		console.log('回调数据', text)
		let data = JSON.parse(text)
		let reqId = data['req_id']
		if (!callbackMap[reqId]) {
			return
		}
		callbackMap[reqId](data)
		delete callbackMap[reqId]
	} catch (ex) {
		console.error('回调错误', ex)
	}
}

//调用 host
const invokeHost = function (method, param, callback) {
	if (typeof window.__kmart_native_host__ === 'undefined') {
		console.warn('[kmartSdk] __kmart_native_host__ not available for method:', method)
		// 如果原生不可用，直接调用回调返回空数据，避免卡死
		if (callback) {
			setTimeout(() => callback({ data: null, error: 'native_not_available' }), 0)
		}
		return
	}
	let reqId = nonce(16)
	callbackMap[reqId] = callback
	let params = JSON.stringify({
		req_id: reqId,
		method: method,
		param: param ?? {}
	})
	window.__kmart_native_host__.postMessage(params)
}

const kmartSdk = () => {
	// 检查原生是否已注入
	nativeConnected = typeof window.__kmart_native_host__ !== 'undefined'
	console.log('[kmartSdk] Initializing, nativeConnected:', nativeConnected)

	// 立即创建 kmartApp 对象（不管原生是否注入）
	window.kmartApp = {
		// 标记 SDK 是否真正连接到原生（getter，实时检查）
		get __nativeConnected() {
			return typeof window.__kmart_native_host__ !== 'undefined'
		},
		location: {
			/** 获取当前坐标 */
			current: function (callback) {
				invokeHost('location.current', null, callback)
			},
			/**
			 * 打开导航选择，并前往导航
			 * lat(float) 纬度
			 * lon(float) 经度
			 * dest(string|null) 目的地
			 * **/
			nav: function (lat, lon, dest, callback) {
				invokeHost('location.nav', { lat: lat, lon: lon, dest: dest }, callback)
			}
		},
		appbar: {
			/**
			 * 设置分享按钮,在appbar 右侧显示分享按钮
			 * img(string)分享图片
			 * title(string)分享标题
			 * desc(string)分享描述
			 * url(string)分享 url
			 * applet(Object|null)小程序分享参数
			 * onShare(Function(result)) 上下文按钮点击回调，result(bool)是否分享成功
			 * */
			setShareButton: function (img, title, desc, url, applet, onShare, callback) {
				window.__on_share_callback__ = onShare
				invokeHost(
					'appbar.setShareButton',
					{
						img: img,
						title: title,
						desc: desc,
						url: url,
						applet: applet,
						onShare: '__on_share_callback__'
					},
					callback
				)
			},
			/**
			 * 在 appbar 右侧设置更多菜单
			 * menu(Array<Object>) 菜单
			 * [
			 *     {
			 *         'icon':(string|null) 图标,
			 *         'label':(string) 标题
			 *     },
			 * ]
			 * onTap(Function(index)) 上下文按钮点击回调，index为点击的按钮索引
			 * */
			setContextMenu: function (menu, onTap, callback) {
				window.__on_context_menu_tap__ = onTap
				invokeHost(
					'appbar.setShareButton',
					{ menu: menu, onTap: '__on_context_menu_tap__' },
					callback
				)
			},

			/**
			 * 在 appbar 右侧按钮
			 * buttons(Array<Object>) 菜单
			 * [
			 *     {
			 *         'icon':(string|null) 图标,
			 *         'label':(string) 标题
			 *         'color':(int|null) eg:0xff000000 按钮文本颜色
			 *     },
			 * ]
			 * onTap(Function(index)) 按钮点击回调，index为点击的按钮索引
			 *
			 * appbar 右侧最大两个按钮，包括分享按钮以及菜单按钮，请视情况使用
			 * */
			setButtons: function (buttons, onTap, callback) {
				window.__on_buttons_tap__ = onTap
				invokeHost('appbar.setButtons', { buttons: buttons, onTap: '__on_buttons_tap__' }, callback)
			},

			/**
			 * 返回模式设置
			 * mode(depend|close) depend为取决于webview是否能回退到上一个 url,这是默认模式；close则立即关闭 webview
			 * showCloseButton(bool) 是否在返回按钮后面显示关闭按钮，默认为 false,如果设置为 true则 mode 设置无效，即为depend
			 * */
			backSetting: function (mode, showCloseButton, callback) {
				invokeHost('appbar.backSetting', { mode: mode, showCloseButton: showCloseButton }, callback)
			},
			/**
			 * 设置导航栏主题色
			 * brightness(dark|light)
			 * **/
			setBrightness: function (brightness, callback) {
				invokeHost('appbar.setBrightness', { brightness: brightness }, callback)
			},
			/**
			 * 设置appbar是否可见
			 * visibility(bool)
			 * **/
			setVisibility: function (visibility, callback) {
				invokeHost('appbar.setVisibility', { visibility: visibility }, callback)
			},
			/**
			 * 设置appbar 标题
			 * title(string)
			 * **/
			setTitle: function (title, callback) {
				invokeHost('appbar.setTitle', { title: title }, callback)
			}
		},
		nav: {
			/**
			 * 关闭当前 webview
			 * backToHome(bool)是否在关闭 webview的同时返回 app主页
			 * goto(string|null) 前往的 url ,可以打开 app的页面如 klife://klife/im/chat
			 * */
			close: function (callback) {
				invokeHost('nav.close', null, callback)
			},
			/**
			 * 关闭Flutter(目前仅用于生活缴费的功能页面)
			 * backToHome(bool)是否在关闭 webview的同时返回 app主页
			 * goto(string|null) 前往的 url ,可以打开 app的页面如 klife://klife/im/chat
			 * */
			closeFlutter: function (url, callback) {
				invokeHost('nav.closePushUrl', { url: url }, callback)
			},
			/**
			 * 是否能打开某个 url
			 * 支持 scheme ,可用于检测是否能打开第三方 app
			 * url
			 * */
			canLaunchUrl: function (url, callback) {
				invokeHost('nav.canLaunchUrl', { url: url }, callback)
			},
			/**
			 * 打开登录页面
			 * 会返回登录后的 token,以及用户信息
			 * */
			login: function (callback) {
				invokeHost('nav.login', null, callback)
			},
			/**
			 * 打开设置中心页面
			 * */
			setting: function (callback) {
				invokeHost('nav.setting', null, callback)
			},
			/**
			 * 打开设置中心页面
			 * phone(string)
			 * */
			phoneCall: function (phone, callback) {
				invokeHost('nav.phoneCall', { phone: phone }, callback)
			},
			/**
			 * to(string|null) 单聊的用户 ID
			 * gid(string|null) 群聊 ID
			 * autoSend(string|null) 自动发送
			 * to或者 gid 都为空时进入 24小时客服界面
			 * */
			im: function (to, gid, autoSend, callback) {
				invokeHost('nav.im', { to: to, gid: gid, autoSend: autoSend }, callback)
			}
		},
		creds: {
			/**
			 * 获取当前 Authorization(token),以及用户基本信息
			 * */
			get: function (callback) {
				invokeHost('creds.get', null, callback)
			}
		},
		pay: {
			/**
			 * 发起支付页面
			 * biz(tk)业务类型，后续支持更多支付业务类型
			 * orderNo(string) 订单号
			 * */
			launch: function (biz, orderNo, redirect, callback) {
				invokeHost('pay.launch', { biz, orderNo, redirect }, callback)
			},
			/**
			 * h5支付结果回调
			 * biz(takeout)业务类型，后续支持更多支付业务类型
			 * orderNo(string) 订单号
			 * result(bool) 支付是否成功
			 * error(string|null) 错误信息
			 * goto(string|null) 可以打开 app的页面如 klife://klife/im/chat,可以打http链接
			 * */
			callback: function (biz, orderNo, result, error, goto, callback) {
				invokeHost(
					'pay.callback',
					{ biz: biz, orderNo: orderNo, result: result, error: error, goto: goto },
					callback
				)
			}
		},
		share: function (url, title, image, posterUrl, callback) {
			invokeHost(
				'share',
				{
					url,
					title,
					image,
					posterUrl
				},
				callback
			)
		},
		/**
		 * 邀请好友分享
		 * content(string) 内容
		 * url(string) 分享链接
		 * title(string) 分享文本
		 * image(string) 图片链接
		 * posterUrl(string) 海报链接
		 * */
		share_invite: function (content, url, title, image, posterUrl, callback) {
			invokeHost(
				'share_invite',
				{
					content,
					url,
					title,
					image,
					posterUrl
				},
				callback
			)
		},
		/**
		 * 多媒体接口
		 * **/
		media: {
			/**
			 * 选择图片
			 * type(1|2),1:图片;2:视频
			 * max(int) 最多选择多少张
			 * */
			select: function (type, max, calback) {
				invokeHost('media.select', { type: type, max: max }, calback)
			}
		},
		device: {
			/**
			 * 获取设备信息
			 * 返回
			 * {
			 *         'device_id':info.deviceId, //设备 id (string|null)
			 *         'version':info.version,//版本  (string|null)
			 *         'build':info.build,//版本构建 (int|null)
			 *         'channel':info.channel,// (string|null)
			 *         '_fchannel':info.fChannel,//(string|null)
			 *         'platform':Platform.operatingSystem//android ,ios
			 *       };
			 * */
			info: function (callback) {
				invokeHost('device.info', null, callback)
			}
		},
		cart: {
			sync: function (callback) {
				invokeHost('cart.sync', null, callback)
			}
		}
	}

	console.log('[kmartSdk] SDK initialized, window.kmartApp created')

	// 如果原生还没注入，启动轮询等待（但不阻塞）
	if (!nativeConnected) {
		console.log('[kmartSdk] Native not connected, starting polling...')
		let waitTime = 0
		const checkInterval = setInterval(() => {
			waitTime += POLL_INTERVAL
			if (typeof window.__kmart_native_host__ !== 'undefined') {
				console.log('[kmartSdk] Native host injected after', waitTime, 'ms')
				nativeConnected = true
				clearInterval(checkInterval)
			} else if (waitTime >= MAX_WAIT_TIME) {
				console.warn('[kmartSdk] Timeout waiting for native host')
				clearInterval(checkInterval)
			}
		}, POLL_INTERVAL)
	}
}

export default kmartSdk
